import React, { useState, useEffect } from 'react';
import { Package, Plus, Eye, Calendar, Weight, Truck, CheckCircle, Clock, Hash, Box, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import { updateReceiptStatus } from '../services/api';

const Receipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  // États pour les modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [receiptDetails, setReceiptDetails] = useState(null);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // États pour la création
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [createFormData, setCreateFormData] = useState({
    client_id: '',
    supplier_name: '',
    expected_date: '',
    shipping_date: '',
    estimated_arrival_date: '',
    carrier: '',
    tracking_number: '',
    packaging_type: 'colis',
    packaging_count: '',
    notes: '',
    products: []
  });

  useEffect(() => {
    fetchReceipts();
    fetchClients();
    fetchProducts();
  }, []);

  const fetchReceipts = async () => {
    try {
      const response = await api.get('/receipts');
      setReceipts(response.data);
    } catch (error) {
      console.error('Erreur chargement réceptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
    }
  };

  const handleViewDetails = async (receiptId) => {
    try {
      const response = await api.get(`/receipts/${receiptId}/details`);
      setReceiptDetails(response.data);
      setSelectedReceipt(receiptId);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Erreur chargement détails:', error);
      alert('Erreur lors du chargement des détails');
    }
  };

  const handleCreateReceipt = async (e) => {
    e.preventDefault();

    if (createFormData.products.length === 0) {
      alert('Veuillez ajouter au moins un produit');
      return;
    }

    try {
      const payload = {
        ...createFormData,
        packaging_count: createFormData.packaging_count ? parseInt(createFormData.packaging_count) : null
      };
      await api.post('/receipts', payload);
      alert('Réception créée avec succès !');
      setShowCreateModal(false);
      fetchReceipts();

      setCreateFormData({
        client_id: '',
        supplier_name: '',
        expected_date: '',
        shipping_date: '',
        estimated_arrival_date: '',
        carrier: '',
        tracking_number: '',
        packaging_type: 'colis',
        packaging_count: '',
        notes: '',
        products: []
      });
    } catch (error) {
      console.error('Erreur création réception:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la création de la réception');
    }
  };

  const addProductToReceipt = () => {
    setCreateFormData(prev => ({
      ...prev,
      products: [...prev.products, { product_id: '', expected_quantity: 1, lot_number: '', expiry_date: '' }]
    }));
  };

  const removeProductFromReceipt = (index) => {
    setCreateFormData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const updateReceiptProduct = (index, field, value) => {
    setCreateFormData(prev => ({
      ...prev,
      products: prev.products.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const handleUpdateStatus = async (receiptId, newStatus) => {
    if (newStatus === 'stocked' && !window.confirm('Confirmer la mise en stock ? L\'inventaire sera mis à jour automatiquement.')) return;
    if (newStatus === 'sav' && !window.confirm('Passer cette réception en SAV ?')) return;
    try {
      await updateReceiptStatus(receiptId, newStatus);
      if (newStatus === 'stocked') {
        alert('Mise en stock confirmée ! Inventaire mis à jour.');
      }
      fetchReceipts();
    } catch (error) {
      console.error('Erreur changement statut:', error);
      alert(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  const handleCancelReceipt = async (receiptId) => {
    if (!cancelReason.trim()) {
      alert('Veuillez saisir un motif d\'annulation');
      return;
    }
    try {
      await api.patch(`/receipts/${receiptId}/status`, { status: 'cancelled', reason: cancelReason });
      setShowCancelForm(false);
      setCancelReason('');
      fetchReceipts();
      const res = await api.get(`/receipts/${receiptId}/details`);
      setReceiptDetails(res.data);
    } catch (error) {
      alert('Erreur : ' + (error.response?.data?.detail || error.message));
    }
  };

  const getNextReceiptStatus = (status) => {
    const next = {
      planned: { status: 'in_transit', text: 'En livraison', color: '#3b82f6', bg: '#dbeafe' },
      in_transit: { status: 'delivered', text: 'Livrée', color: '#8b5cf6', bg: '#ede9fe' },
      delivered: { status: 'inspected', text: 'Contrôlée', color: '#06b6d4', bg: '#cffafe' },
      inspected: { status: 'stocked', text: 'Mise en stock', color: '#10b981', bg: '#d1fae5' },
    };
    return next[status] || null;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      planned: { text: 'Planifiée', color: '#f59e0b', bg: '#fef3c7' },
      in_transit: { text: 'En livraison', color: '#3b82f6', bg: '#dbeafe' },
      delivered: { text: 'Livrée', color: '#8b5cf6', bg: '#ede9fe' },
      inspected: { text: 'Contrôlée', color: '#06b6d4', bg: '#cffafe' },
      stocked: { text: 'Mise en stock', color: '#10b981', bg: '#d1fae5' },
      sav: { text: 'SAV', color: '#ef4444', bg: '#fee2e2' },
      cancelled: { text: 'Annulé', color: '#6b7280', bg: '#f3f4f6' },
    };
    return statusConfig[status] || statusConfig.planned;
  };

  const filteredReceipts = filterStatus === 'all'
    ? receipts
    : receipts.filter(receipt => receipt.status === filterStatus);

  const statusCounts = {
    all: receipts.length,
    planned: receipts.filter(r => r.status === 'planned').length,
    in_transit: receipts.filter(r => r.status === 'in_transit').length,
    delivered: receipts.filter(r => r.status === 'delivered').length,
    inspected: receipts.filter(r => r.status === 'inspected').length,
    stocked: receipts.filter(r => r.status === 'stocked').length,
    sav: receipts.filter(r => r.status === 'sav').length,
    cancelled: receipts.filter(r => r.status === 'cancelled').length,
  };

  if (loading) {
    return <div style={styles.loading}>Chargement des réceptions...</div>;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Gestion des Réceptions</h1>
          <p style={styles.subtitle}>{filteredReceipts.length} réception(s)</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} style={styles.createButton}>
          <Plus size={20} />
          Créer une Réception
        </button>
      </div>

      {/* Filtres par statut */}
      <div style={styles.filterContainer}>
        <button
          onClick={() => setFilterStatus('all')}
          style={filterStatus === 'all' ? styles.filterButtonActive : styles.filterButton}
        >
          Toutes ({statusCounts.all})
        </button>
        <button
          onClick={() => setFilterStatus('planned')}
          style={filterStatus === 'planned' ? styles.filterButtonActive : styles.filterButton}
        >
          Planifiées ({statusCounts.planned})
        </button>
        <button
          onClick={() => setFilterStatus('in_transit')}
          style={filterStatus === 'in_transit' ? styles.filterButtonActive : styles.filterButton}
        >
          En livraison ({statusCounts.in_transit})
        </button>
        <button
          onClick={() => setFilterStatus('delivered')}
          style={filterStatus === 'delivered' ? styles.filterButtonActive : styles.filterButton}
        >
          Livrées ({statusCounts.delivered})
        </button>
        <button
          onClick={() => setFilterStatus('inspected')}
          style={filterStatus === 'inspected' ? styles.filterButtonActive : styles.filterButton}
        >
          Contrôlées ({statusCounts.inspected})
        </button>
        <button
          onClick={() => setFilterStatus('stocked')}
          style={filterStatus === 'stocked' ? styles.filterButtonActive : styles.filterButton}
        >
          En stock ({statusCounts.stocked})
        </button>
        <button
          onClick={() => setFilterStatus('sav')}
          style={filterStatus === 'sav' ? styles.filterButtonActive : styles.filterButton}
        >
          SAV ({statusCounts.sav})
        </button>
        <button
          onClick={() => setFilterStatus('cancelled')}
          style={filterStatus === 'cancelled' ? styles.filterButtonActive : styles.filterButton}
        >
          Annulé ({statusCounts.cancelled})
        </button>
      </div>

      {/* Grille des réceptions */}
      <div style={styles.receiptsGrid}>
        {filteredReceipts.map(receipt => {
          const status = getStatusBadge(receipt.status);

          return (
            <div key={receipt.id} style={styles.receiptCard}>
              <div style={styles.receiptHeader}>
                <div>
                  <h3 style={styles.receiptNumber}>{receipt.receipt_number}</h3>
                  <p style={styles.receiptSupplier}>
                    <Truck size={16} />
                    {receipt.supplier_name}
                  </p>
                  {receipt.client_name && (
                    <p style={styles.receiptClient}>Client: {receipt.client_name}</p>
                  )}
                </div>
                <span style={{...styles.badge, backgroundColor: status.bg, color: status.color}}>
                  {status.text}
                </span>
              </div>

              <div style={styles.receiptBody}>
                {receipt.expected_date && (
                  <div style={styles.receiptInfo}>
                    <Calendar size={16} color="#64748b" />
                    <div>
                      <div style={styles.receiptLabel}>Date attendue</div>
                      <div style={styles.receiptValue}>
                        {new Date(receipt.expected_date).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>
                )}

                {receipt.shipping_date && (
                  <div style={styles.receiptInfo}>
                    <Calendar size={16} color="#64748b" />
                    <div>
                      <div style={styles.receiptLabel}>Date d'envoi</div>
                      <div style={styles.receiptValue}>
                        {new Date(receipt.shipping_date).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>
                )}

                {receipt.carrier && (
                  <div style={styles.receiptInfo}>
                    <Truck size={16} color="#64748b" />
                    <div>
                      <div style={styles.receiptLabel}>Transporteur</div>
                      <div style={styles.receiptValue}>{receipt.carrier}</div>
                    </div>
                  </div>
                )}

                {receipt.tracking_number && (
                  <div style={styles.receiptInfo}>
                    <Hash size={16} color="#64748b" />
                    <div>
                      <div style={styles.receiptLabel}>N° de suivi</div>
                      <div style={styles.receiptValue}>{receipt.tracking_number}</div>
                    </div>
                  </div>
                )}

                {receipt.packaging_count > 0 && (
                  <div style={styles.receiptInfo}>
                    <Box size={16} color="#64748b" />
                    <div>
                      <div style={styles.receiptLabel}>Colisage</div>
                      <div style={styles.receiptValue}>
                        {receipt.packaging_count} {receipt.packaging_type || 'colis'}
                      </div>
                    </div>
                  </div>
                )}

                {receipt.received_date && (
                  <div style={styles.receiptInfo}>
                    <CheckCircle size={16} color="#10b981" />
                    <div>
                      <div style={styles.receiptLabel}>Date de réception</div>
                      <div style={styles.receiptValue}>
                        {new Date(receipt.received_date).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>
                )}

                {receipt.notes && (
                  <div style={styles.receiptNotes}>
                    <span style={styles.notesLabel}>Notes:</span> {receipt.notes}
                  </div>
                )}
              </div>

              <div style={styles.receiptFooter}>
                <div style={{display: 'flex', gap: '0.5rem'}}>
                  <button
                    onClick={() => handleViewDetails(receipt.id)}
                    style={styles.detailsButton}
                  >
                    <Eye size={16} />
                    Détails
                  </button>
                  {(() => {
                    const next = getNextReceiptStatus(receipt.status);
                    if (next) return (
                      <button
                        onClick={() => handleUpdateStatus(receipt.id, next.status)}
                        style={{...styles.statusButton, backgroundColor: next.bg, color: next.color, borderColor: next.color}}
                      >
                        {next.text}
                      </button>
                    );
                    return null;
                  })()}
                  {receipt.status !== 'sav' && receipt.status !== 'stocked' && receipt.status !== 'cancelled' && (
                    <button
                      onClick={() => handleUpdateStatus(receipt.id, 'sav')}
                      style={styles.savButton}
                    >
                      <AlertTriangle size={14} />
                      SAV
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredReceipts.length === 0 && (
        <div style={styles.emptyState}>
          <Package size={48} color="#cbd5e1" />
          <p style={styles.emptyText}>Aucune réception trouvée</p>
        </div>
      )}

      {/* Modal de Création */}
      {showCreateModal && (
        <div style={styles.modal} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Créer une Nouvelle Réception</h2>
              <button onClick={() => setShowCreateModal(false)} style={styles.closeButton}>
                ×
              </button>
            </div>

            <form onSubmit={handleCreateReceipt} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Client *</label>
                <select
                  value={createFormData.client_id}
                  onChange={(e) => setCreateFormData({...createFormData, client_id: e.target.value})}
                  style={styles.select}
                  required
                >
                  <option value="">Sélectionner un client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Fournisseur *</label>
                  <input
                    type="text"
                    value={createFormData.supplier_name}
                    onChange={(e) => setCreateFormData({...createFormData, supplier_name: e.target.value})}
                    style={styles.input}
                    placeholder="Nom du fournisseur"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date attendue</label>
                  <input
                    type="date"
                    value={createFormData.expected_date}
                    onChange={(e) => setCreateFormData({...createFormData, expected_date: e.target.value})}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date d'envoi</label>
                  <input
                    type="date"
                    value={createFormData.shipping_date}
                    onChange={(e) => setCreateFormData({...createFormData, shipping_date: e.target.value})}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date d'arrivée estimée</label>
                  <input
                    type="date"
                    value={createFormData.estimated_arrival_date}
                    onChange={(e) => setCreateFormData({...createFormData, estimated_arrival_date: e.target.value})}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Transporteur</label>
                  <input
                    type="text"
                    value={createFormData.carrier}
                    onChange={(e) => setCreateFormData({...createFormData, carrier: e.target.value})}
                    style={styles.input}
                    placeholder="Nom du transporteur"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>N° de suivi</label>
                  <input
                    type="text"
                    value={createFormData.tracking_number}
                    onChange={(e) => setCreateFormData({...createFormData, tracking_number: e.target.value})}
                    style={styles.input}
                    placeholder="Numéro de suivi"
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Type de colisage</label>
                  <select
                    value={createFormData.packaging_type}
                    onChange={(e) => setCreateFormData({...createFormData, packaging_type: e.target.value})}
                    style={styles.select}
                  >
                    <option value="colis">Colis</option>
                    <option value="palettes">Palettes</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nombre</label>
                  <input
                    type="number"
                    value={createFormData.packaging_count}
                    onChange={(e) => setCreateFormData({...createFormData, packaging_count: e.target.value})}
                    style={styles.input}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>

              <div style={styles.section}>
                <div style={styles.sectionHeader}>
                  <h3 style={styles.sectionTitle}>Produits Attendus *</h3>
                  <button type="button" onClick={addProductToReceipt} style={styles.addProductButton}>
                    <Plus size={16} />
                    Ajouter un Produit
                  </button>
                </div>

                {createFormData.products.length === 0 && (
                  <p style={styles.helpText}>Cliquez sur "Ajouter un Produit" pour commencer</p>
                )}

                {createFormData.products.map((product, index) => (
                  <div key={index} style={styles.productGroup}>
                    <div style={styles.productRow}>
                      <select
                        value={product.product_id}
                        onChange={(e) => updateReceiptProduct(index, 'product_id', e.target.value)}
                        style={{...styles.select, flex: 2}}
                        required
                      >
                        <option value="">Sélectionner un produit</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                        ))}
                      </select>

                      <input
                        type="number"
                        value={product.expected_quantity}
                        onChange={(e) => updateReceiptProduct(index, 'expected_quantity', parseInt(e.target.value) || 1)}
                        style={{...styles.input, flex: 1}}
                        min="1"
                        placeholder="Quantité"
                        required
                      />

                      <button
                        type="button"
                        onClick={() => removeProductFromReceipt(index)}
                        style={styles.removeButton}
                        title="Retirer ce produit"
                      >
                        ×
                      </button>
                    </div>

                    <div style={styles.productRow}>
                      <input
                        type="text"
                        value={product.lot_number}
                        onChange={(e) => updateReceiptProduct(index, 'lot_number', e.target.value)}
                        style={{...styles.input, flex: 1}}
                        placeholder="Numéro de lot (optionnel)"
                      />

                      <input
                        type="date"
                        value={product.expiry_date}
                        onChange={(e) => updateReceiptProduct(index, 'expiry_date', e.target.value)}
                        style={{...styles.input, flex: 1}}
                        placeholder="Date d'expiration"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes</label>
                <textarea
                  value={createFormData.notes}
                  onChange={(e) => setCreateFormData({...createFormData, notes: e.target.value})}
                  style={styles.textarea}
                  rows="3"
                  placeholder="Notes additionnelles sur cette réception..."
                />
              </div>

              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowCreateModal(false)} style={styles.cancelButton}>
                  Annuler
                </button>
                <button type="submit" style={styles.submitButton}>
                  Créer la Réception
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Détails */}
      {showDetailsModal && receiptDetails && (
        <div style={styles.modal} onClick={() => setShowDetailsModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Détails de la Réception</h2>
              <button onClick={() => setShowDetailsModal(false)} style={styles.closeButton}>
                ×
              </button>
            </div>

            <div style={styles.detailsContainer}>
              <div style={styles.detailsSection}>
                <h3 style={styles.detailsSectionTitle}>Informations Générales</h3>
                <div style={styles.detailsGrid}>
                  <div style={styles.detailItem}>
                    <Truck size={20} color="#64748b" />
                    <div>
                      <div style={styles.detailLabel}>Fournisseur</div>
                      <div style={styles.detailValue}>{receiptDetails.receipt.supplier_name}</div>
                    </div>
                  </div>

                  {receiptDetails.client && (
                    <div style={styles.detailItem}>
                      <Package size={20} color="#64748b" />
                      <div>
                        <div style={styles.detailLabel}>Client</div>
                        <div style={styles.detailValue}>{receiptDetails.client.name}</div>
                      </div>
                    </div>
                  )}

                  {receiptDetails.receipt.expected_date && (
                    <div style={styles.detailItem}>
                      <Calendar size={20} color="#64748b" />
                      <div>
                        <div style={styles.detailLabel}>Date attendue</div>
                        <div style={styles.detailValue}>
                          {new Date(receiptDetails.receipt.expected_date).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>
                  )}

                  {receiptDetails.receipt.shipping_date && (
                    <div style={styles.detailItem}>
                      <Calendar size={20} color="#3b82f6" />
                      <div>
                        <div style={styles.detailLabel}>Date d'envoi</div>
                        <div style={styles.detailValue}>
                          {new Date(receiptDetails.receipt.shipping_date).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>
                  )}

                  {receiptDetails.receipt.estimated_arrival_date && (
                    <div style={styles.detailItem}>
                      <Calendar size={20} color="#f59e0b" />
                      <div>
                        <div style={styles.detailLabel}>Arrivée estimée</div>
                        <div style={styles.detailValue}>
                          {new Date(receiptDetails.receipt.estimated_arrival_date).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    </div>
                  )}

                  {receiptDetails.receipt.carrier && (
                    <div style={styles.detailItem}>
                      <Truck size={20} color="#3b82f6" />
                      <div>
                        <div style={styles.detailLabel}>Transporteur</div>
                        <div style={styles.detailValue}>{receiptDetails.receipt.carrier}</div>
                      </div>
                    </div>
                  )}

                  {receiptDetails.receipt.tracking_number && (
                    <div style={styles.detailItem}>
                      <Hash size={20} color="#64748b" />
                      <div>
                        <div style={styles.detailLabel}>N° de suivi</div>
                        <div style={styles.detailValue}>{receiptDetails.receipt.tracking_number}</div>
                      </div>
                    </div>
                  )}

                  {receiptDetails.receipt.packaging_count > 0 && (
                    <div style={styles.detailItem}>
                      <Box size={20} color="#64748b" />
                      <div>
                        <div style={styles.detailLabel}>Colisage</div>
                        <div style={styles.detailValue}>
                          {receiptDetails.receipt.packaging_count} {receiptDetails.receipt.packaging_type || 'colis'}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={styles.detailItem}>
                    <Package size={20} color="#64748b" />
                    <div>
                      <div style={styles.detailLabel}>Produits attendus</div>
                      <div style={styles.detailValue}>{receiptDetails.total_products} unités</div>
                    </div>
                  </div>

                  <div style={styles.detailItem}>
                    <Weight size={20} color="#64748b" />
                    <div>
                      <div style={styles.detailLabel}>Poids Total</div>
                      <div style={styles.detailValue}>{receiptDetails.total_weight} g</div>
                    </div>
                  </div>
                </div>

                {receiptDetails.receipt.notes && (
                  <div style={styles.notesBox}>
                    <strong>Notes:</strong>
                    <p>{receiptDetails.receipt.notes}</p>
                  </div>
                )}
              </div>

              {/* Suivi des statuts */}
              <div style={styles.detailsSection}>
                <h3 style={styles.detailsSectionTitle}>Suivi des Statuts</h3>
                <div style={styles.timeline}>
                  {[
                    { key: 'created_at', label: 'Réception créée', color: '#64748b' },
                    { key: 'in_transit_date', label: 'En livraison', color: '#3b82f6' },
                    { key: 'delivered_date', label: 'Livrée', color: '#8b5cf6' },
                    { key: 'inspected_date', label: 'Contrôlée', color: '#06b6d4' },
                    { key: 'stocked_date', label: 'Mise en stock', color: '#10b981' },
                    { key: 'sav_date', label: 'SAV', color: '#ef4444' },
                    { key: 'cancelled_date', label: 'Annulé', color: '#6b7280' },
                  ].map(step => {
                    const date = receiptDetails.receipt[step.key];
                    return (
                      <div key={step.key} style={styles.timelineItem}>
                        <div style={{
                          ...styles.timelineDot,
                          backgroundColor: date ? step.color : '#e2e8f0',
                        }} />
                        <div style={{flex: 1}}>
                          <div style={{
                            ...styles.timelineLabel,
                            color: date ? '#0f172a' : '#94a3b8',
                            fontWeight: date ? '600' : '400',
                          }}>
                            {step.label}
                          </div>
                          <div style={styles.timelineDate}>
                            {date ? new Date(date).toLocaleString('fr-FR') : '—'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Motif d'annulation */}
              {receiptDetails.receipt.cancellation_reason && (
                <div style={{...styles.detailsSection, backgroundColor: '#f9fafb', borderRadius: '12px', padding: '16px'}}>
                  <h3 style={{...styles.detailsSectionTitle, color: '#6b7280'}}>Motif d'annulation</h3>
                  <p style={{margin: 0, color: '#374151', fontSize: '14px'}}>{receiptDetails.receipt.cancellation_reason}</p>
                  {receiptDetails.receipt.cancelled_by && (
                    <p style={{margin: '8px 0 0', color: '#9ca3af', fontSize: '12px'}}>Par : {receiptDetails.receipt.cancelled_by}</p>
                  )}
                </div>
              )}

              {/* Bouton annuler */}
              {receiptDetails.receipt.status !== 'cancelled' && receiptDetails.receipt.status !== 'stocked' && (
                <div style={styles.detailsSection}>
                  {!showCancelForm ? (
                    <button
                      onClick={() => setShowCancelForm(true)}
                      style={{padding: '10px 20px', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'}}
                    >
                      Annuler cette réception
                    </button>
                  ) : (
                    <div style={{border: '1px solid #fca5a5', borderRadius: '12px', padding: '16px', backgroundColor: '#fef2f2'}}>
                      <h4 style={{margin: '0 0 12px', color: '#dc2626', fontSize: '14px', fontWeight: '600'}}>Motif d'annulation (obligatoire)</h4>
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Saisissez le motif d'annulation..."
                        style={{width: '100%', minHeight: '80px', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box'}}
                      />
                      <div style={{display: 'flex', gap: '8px', marginTop: '12px'}}>
                        <button
                          onClick={() => handleCancelReceipt(receiptDetails.receipt.id)}
                          style={{padding: '8px 16px', backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px'}}
                        >
                          Confirmer l'annulation
                        </button>
                        <button
                          onClick={() => { setShowCancelForm(false); setCancelReason(''); }}
                          style={{padding: '8px 16px', backgroundColor: '#fff', color: '#64748b', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '13px'}}
                        >
                          Retour
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={styles.detailsSection}>
                <h3 style={styles.detailsSectionTitle}>Produits</h3>
                <table style={styles.detailsTable}>
                  <thead>
                    <tr>
                      <th style={styles.detailsTableHeader}>Produit</th>
                      <th style={styles.detailsTableHeader}>SKU</th>
                      <th style={styles.detailsTableHeader}>Qté Attendue</th>
                      <th style={styles.detailsTableHeader}>Qté Reçue</th>
                      <th style={styles.detailsTableHeader}>N° Lot</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiptDetails.lines.map((line, index) => (
                      <tr key={index} style={index % 2 === 0 ? styles.detailsTableRow : styles.detailsTableRowEven}>
                        <td style={styles.detailsTableCell}>{line.product?.name || 'N/A'}</td>
                        <td style={styles.detailsTableCell}>
                          <span style={styles.skuBadge}>{line.product?.sku || 'N/A'}</span>
                        </td>
                        <td style={styles.detailsTableCell}>{line.expected_quantity}</td>
                        <td style={styles.detailsTableCell}>
                          <strong>{line.received_quantity}</strong>
                        </td>
                        <td style={styles.detailsTableCell}>
                          {line.lot_number || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#0f172a',
    margin: 0,
  },
  subtitle: {
    color: '#64748b',
    marginTop: '0.5rem',
  },
  createButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.5rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  filterContainer: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '2rem',
    flexWrap: 'wrap',
  },
  filterButton: {
    padding: '0.5rem 1rem',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    color: '#64748b',
  },
  filterButtonActive: {
    padding: '0.5rem 1rem',
    backgroundColor: '#3b82f6',
    border: '1px solid #3b82f6',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    color: 'white',
    fontWeight: '500',
  },
  receiptsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '1.5rem',
  },
  receiptCard: {
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    border: '1px solid #e2e8f0',
    padding: '1.5rem',
  },
  receiptHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #f1f5f9',
  },
  receiptNumber: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#0f172a',
    margin: 0,
  },
  receiptSupplier: {
    fontSize: '0.875rem',
    color: '#64748b',
    marginTop: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  receiptClient: {
    fontSize: '0.75rem',
    color: '#3b82f6',
    marginTop: '0.25rem',
    fontWeight: '500',
  },
  badge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
  },
  receiptBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  receiptInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  receiptLabel: {
    fontSize: '0.75rem',
    color: '#64748b',
  },
  receiptValue: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#0f172a',
  },
  receiptNotes: {
    fontSize: '0.875rem',
    color: '#475569',
    backgroundColor: '#f8fafc',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    marginTop: '0.5rem',
  },
  notesLabel: {
    fontWeight: '500',
    color: '#334155',
  },
  receiptFooter: {
    borderTop: '1px solid #f1f5f9',
    paddingTop: '1rem',
  },
  detailsButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#f8fafc',
    color: '#3b82f6',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    flex: 1,
    justifyContent: 'center',
  },
  statusButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.5rem 0.75rem',
    border: '1px solid',
    borderRadius: '0.5rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    flex: 1,
    justifyContent: 'center',
  },
  savButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fee2e2',
    color: '#ef4444',
    border: '1px solid #fca5a5',
    borderRadius: '0.5rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    justifyContent: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: '4rem 2rem',
  },
  emptyText: {
    marginTop: '1rem',
    color: '#64748b',
    fontSize: '1rem',
  },
  loading: {
    textAlign: 'center',
    padding: '4rem',
    fontSize: '1.125rem',
    color: '#64748b',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 10,
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#0f172a',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '0.5rem',
    border: 'none',
    backgroundColor: '#f1f5f9',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#64748b',
  },
  form: {
    padding: '1.5rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
    flex: 1,
  },
  formRow: {
    display: 'flex',
    gap: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '0.5rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    outline: 'none',
    backgroundColor: 'white',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  section: {
    marginBottom: '2rem',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#0f172a',
  },
  addProductButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    color: '#3b82f6',
  },
  productGroup: {
    backgroundColor: '#f8fafc',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
  },
  productRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    alignItems: 'center',
  },
  removeButton: {
    width: '40px',
    height: '40px',
    borderRadius: '0.5rem',
    border: '1px solid #fee2e2',
    backgroundColor: '#fef2f2',
    color: '#ef4444',
    fontSize: '1.5rem',
    cursor: 'pointer',
    flexShrink: 0,
  },
  helpText: {
    fontSize: '0.875rem',
    color: '#64748b',
    fontStyle: 'italic',
  },
  modalFooter: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e2e8f0',
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
  detailsContainer: {
    padding: '1.5rem',
  },
  detailsSection: {
    marginBottom: '2rem',
  },
  detailsSectionTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: '1rem',
    paddingBottom: '0.5rem',
    borderBottom: '2px solid #e2e8f0',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.5rem',
    marginBottom: '1.5rem',
  },
  detailItem: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
  },
  detailLabel: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginBottom: '0.25rem',
  },
  detailValue: {
    fontSize: '1rem',
    fontWeight: '500',
    color: '#0f172a',
  },
  notesBox: {
    backgroundColor: '#f8fafc',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
  },
  detailsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
  },
  detailsTableHeader: {
    padding: '0.75rem',
    textAlign: 'left',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#475569',
    borderBottom: '2px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  detailsTableRow: {
    borderBottom: '1px solid #f1f5f9',
  },
  detailsTableRowEven: {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #f1f5f9',
  },
  detailsTableCell: {
    padding: '0.75rem',
    fontSize: '0.875rem',
    color: '#334155',
  },
  skuBadge: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    backgroundColor: '#f1f5f9',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    color: '#64748b',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  timelineItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '0.75rem 0',
    borderLeft: '2px solid #e2e8f0',
    marginLeft: '7px',
    paddingLeft: '1.25rem',
    position: 'relative',
  },
  timelineDot: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    position: 'absolute',
    left: '-8px',
    top: '0.85rem',
    border: '2px solid white',
    boxShadow: '0 0 0 2px #e2e8f0',
  },
  timelineLabel: {
    fontSize: '0.875rem',
  },
  timelineDate: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: '0.125rem',
  },
};

export default Receipts;
